import { FC, useMemo } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import { formatNumber } from 'utils/formats';

import MandatoryField from 'modules/_shared/components/MandatoryField';
import CustomAsyncSelect from 'modules/_shared/components/CustomAsyncSelect';
import InputNumber from 'modules/_shared/components/InputNumber';
import InputRadio from 'modules/_shared/components/InputRadio';
import InputDate from 'modules/_shared/components/InputDate';
import InputCheckbox from 'modules/_shared/components/InputCheckbox';

import { TradingOfferType } from 'types/TradingOffer';
import { useAddTradingOfferPage } from '../context/AddTradingOfferPageContext';
import { addTradingOfferLabels } from '../constants/addTradingOfferLabels';

const AddTradingOfferDataSection: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useAddTradingOfferPage();

  const labels = useMemo(
    () => addTradingOfferLabels[data.tradingOfferType as TradingOfferType],
    [data.tradingOfferType]
  );

  const isSellOffer = useMemo(
    () => data.tradingOfferType === TradingOfferType.SELL,
    [data.tradingOfferType]
  );

  const sharesAfterSale = data.partnerCurrentSharesCount
    ? data.partnerCurrentSharesCount - data.sharesToTransfer
    : 0;

  return (
    <div className="col-12 col-lg-8">
      {isSellOffer && (
        <div className="card card-bordered sp-plan mb-4">
          <div className="sp-plan-info card-inner">
            <h6 className="title">{t('AvailableShares')}</h6>
          </div>
          <div className="sp-plan-desc card-inner">
            <ul className="row gx-1 text-left align-items-end">
              <li className="col-12 col-lg-6">
                <p>
                  <span className="text-soft">{t('CurrentShares')}</span>
                  {formatNumber(data.partnerCurrentSharesCount)}
                </p>
              </li>

              <li className="col-12 col-lg-6">
                <p>
                  <span className="text-soft">{t('SharesAfterSale')}</span>
                  <span className="text-left text-primary">
                    {formatNumber(sharesAfterSale)}
                  </span>
                </p>
              </li>
            </ul>
          </div>
        </div>
      )}

      {!data.isPartnerView && (
        <div className="form-group">
          <label className="form-label" htmlFor="partner-select-input">
            {t(labels.SELECT_PARTNER)} <MandatoryField />
          </label>
          <div className="form-control-wrap">
            <CustomAsyncSelect
              id="partner-select-input"
              value={data.selectedPartnerOption}
              onChange={actions.handleChangePartner}
              onFetchData={actions.handleSearchPartnerByTerm}
              placeholder={t('SearchPartner')}
              minimumInputLength={3}
              noDataLabelKey="NoPartnersHaveBeenFound"
              loadingLabelKey="LoadingPartners"
              errorLabelKey="ErrorLoadingPartners"
              defaultOptions={data.partnerOptions}
            />
          </div>
        </div>
      )}

      <div className={`form-group ${data.isPartnerView ? 'mt-4' : ''}`}>
        <label className="form-label" htmlFor="class-select-input">
          {`${t(labels.SELECT_CLASS)} (${t('Optional')})`}
        </label>
        <div className="form-control-wrap">
          <CustomAsyncSelect
            id="class-select-input"
            value={data.selectedHoldingClassOption}
            onChange={actions.handleChangeHoldingClass}
            onFetchData={actions.handleSearchHoldingClassByTerm}
            placeholder={t('SearchClass')}
            minimumInputLength={1}
            noDataLabelKey="NoClassesHaveBeenFound"
            loadingLabelKey="LoadingClasses"
            errorLabelKey="ErrorLoadingClasses"
            defaultOptions={data.holdingClassOptions}
          />
        </div>
      </div>

      <InputNumber
        id="shares-to-transfer-input"
        label={t(labels.SHARES_TO_TRANSFER)}
        infoLabel={t(labels.SHARES_TO_TRANSFER_INFO, {
          count: data.maxSharesToTransfer,
        })}
        value={data.sharesToTransfer}
        decimalScale={2}
        max={data.maxSharesToTransfer}
        onChange={actions.handleChangeSharesToTransfer}
        required
      />

      {isSellOffer && (
        <InputCheckbox
          id="accept-partial-sale-checkbox"
          label={t('IAcceptPartialSaleOfShares')}
          checked={data.isPartialAmountEnabled}
          onChange={actions.handleChangeIsPartialAmountEnabled}
          marginBottom={3}
        />
      )}

      <div className="form-group row">
        <div className="col-6">
          <InputNumber
            id="price"
            label={t(labels.PRICE)}
            infoLabel={
              data.isPricePerShare
                ? t(labels.PRICE_PER_SHARE_INFO)
                : t(labels.PRICE_TOTAL_INFO)
            }
            value={data.price}
            iconRight="icon ni ni-sign-euro"
            decimalScale={2}
            onChange={actions.handleChangePrice}
            required
          />
        </div>

        {isSellOffer && (
          <>
            <div className="col-3 d-flex align-items-center">
              <InputRadio
                id="price-per-share-radio"
                checked={data.isPricePerShare}
                label={t('PricePerShare')}
                onChange={actions.handleChangeIsPricePerShare}
              />
            </div>

            <div className="col-3 d-flex align-items-center">
              <InputRadio
                id="total-offer-price-radio"
                checked={!data.isPricePerShare}
                label={t('TotalOfferPrice')}
                onChange={actions.handleChangeIsPricePerShare}
              />
            </div>
          </>
        )}
      </div>

      <div className="row">
        <div className="col-12 mt-2 mb-2">
          <h6>{t('TradingOfferValidityPeriod')}</h6>
        </div>

        <div className="col-6">
          <InputDate
            id="startDate"
            label={t('StartDate')}
            value={data.startDate}
            onChange={actions.handleChangeStartDate}
            required
            lgSize
          />
        </div>

        <div className="col-6">
          <InputDate
            id="endDate"
            label={t('EndDate')}
            value={data.endDate}
            onChange={actions.handleChangeEndDate}
            minDate={data.startDate || undefined}
            required
            lgSize
          />
        </div>

        <div className="col-12">
          <div className="form-note pt-1">
            {t('TradingOfferActiveDatesInfo')}
          </div>
        </div>
      </div>

      <div className="h-100px" />
    </div>
  );
};

export default AddTradingOfferDataSection;
